import Filter from '@components/Filter';
import { Option, TranslationSetupForm } from '@customTypes';
import useCustomTranslation from '@hooks/useCustomTranslation';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Box,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { useFormContext, useWatch } from 'react-hook-form';
import CloseIcon from '@mui/icons-material/Close';
import { useAddGeneralPoolLanguageMutation, useAddProjectPoolLanguageMutation } from '@services/api/translation.api';
import { GENERAL_SETUP_LANGUAGE_OPTIONS, SETUP_LANGUAGE_TABS } from '@constants';
import { useAppSelector } from '@hooks/reduxHooks';

import styles from './TranslationSetupDialog.module.scss';

interface AddLanguageDialogProps {
  isOpen: boolean;
  handleClose: () => void;
  options: Option[];
}

export const AddLanguageDialog = ({ isOpen, handleClose, options }: AddLanguageDialogProps) => {
  const { getTranslation } = useCustomTranslation();
  const { control, setValue } = useFormContext<TranslationSetupForm>();
  const { selectedProject } = useAppSelector((state) => state.projects);

  const [targetAddGeneralLanguage, { isLoading: isLoadingAddGeneralLanguage }] = useAddGeneralPoolLanguageMutation();
  const [targetAddProjectLanguage, { isLoading: isLoadingAddProjectLanguage }] = useAddProjectPoolLanguageMutation();

  const language = useWatch({
    control,
    name: 'language-add',
  }) as string;

  const projectAnalysisActiveTab = useWatch({
    control,
    name: 'projectAnalysisActiveTab',
  });

  const projectTranslationActiveTab = useWatch({
    control,
    name: 'projectTranslationActiveTab',
  });

  const handleProceed = async () => {
    try {
      if (projectTranslationActiveTab === +SETUP_LANGUAGE_TABS.PROJECT) {
        await targetAddProjectLanguage({ languageId: language, businessId: selectedProject?.selectedBusinessId });
      } else {
        await targetAddGeneralLanguage({
          languageId: language,
          languagePool: GENERAL_SETUP_LANGUAGE_OPTIONS[projectAnalysisActiveTab],
        });
      }
      setValue('language', language);
      handleClose();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Dialog
      open={isOpen}
      keepMounted={false}
      aria-labelledby="language-dialog"
      className={styles.addLanguageDialogContainer}
    >
      <DialogTitle id="language-dialog" className={styles.dialogTitle}>
        <Box>{getTranslation('LangSel.Add', 'addLanguage.new.title')}</Box>
        <IconButton aria-label="close" onClick={handleClose}>
          <CloseIcon color="action" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <DialogContentText>{getTranslation('LangSel.Explain', 'addLanguage.new.description')}</DialogContentText>
        <Box className={styles.filterContainer}>
          <Filter control={control} name="language-add" options={options} />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          size="medium"
          onClick={handleClose}
          disabled={isLoadingAddGeneralLanguage || isLoadingAddProjectLanguage}
        >
          {getTranslation('CvaInput.WizzCancel', 'buttons.cancel')}
        </Button>
        <Button
          variant="contained"
          size="medium"
          onClick={handleProceed}
          endIcon={(isLoadingAddGeneralLanguage || isLoadingAddProjectLanguage) && <CircularProgress />}
          disabled={isLoadingAddGeneralLanguage || isLoadingAddProjectLanguage}
        >
          {getTranslation('CvaProj.Setup57', 'addLanguage.new.action')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
